import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Menu as MenuIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../lib/appConfig';
import { useTheme } from '../hooks/useTheme';
import { useNotifications } from '../hooks/useNotifications';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import PageHeader from '../ui/PageHeader';
import Sidebar from '../ui/Sidebar';
import TopBar from '../ui/TopBar';
import NotificationsPanel from './NotificationsPanel';
import ShellPalette from './ShellPalette';
import { UploadProvider } from './uploads';
import { CLIENT_NAV, hasTalentRole, isAppPath, isMigrated, isWorkbench, staffNav, workbenchItem } from './nav';
import { usePageHeaderSpec } from './pageHeader';
import { PortalTierToggle, usePortalTier } from './portalTier';
import './shell.css';
import './page.css';

export function useMedia(query) {
  const [hit, setHit] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query); const on = () => setHit(m.matches);
    m.addEventListener('change', on); return () => m.removeEventListener('change', on);
  }, [query]);
  return hit;
}

// The sidebar mode a user chose on Console, persisted per user on this device.
function usePersistedMode(userId) {
  const key = `hf-sidebar-mode:${userId}`;
  const [mode, setMode] = useState(() => { try { return localStorage.getItem(key) || 'expanded'; } catch { return 'expanded'; } });
  const set = useCallback(m => { setMode(m); try { localStorage.setItem(key, m); } catch { /* private mode */ } }, [key]);
  return [mode, set];
}

function PageHeaderSlot() {
  const spec = usePageHeaderSpec();
  return spec ? <PageHeader title={spec.title} meta={spec.meta} actions={spec.actions} filters={spec.filters} /> : null;
}

// Every logged-in route's shell (DESIGN rev 2.1 §2.5, §3). Page bodies render unchanged inside it until their own order.
export default function AppShell() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const { aiProductionEnabled } = useAppConfig();
  const roles = user.roles || [];
  if (roles.length === 1 && roles[0] === 'Talent') return <PortalShell user={user} onSignOut={logout} />;
  // HD-11.1: a client on a path the app does not have gets "Page not found" in the Portal frame too
  if (user.isExternal && (PORTAL_RAIL_PATHS.has(pathname) || !isAppPath(pathname, { aiEnabled: aiProductionEnabled }))) return <PortalRail user={user} logout={logout} />;
  return <RailShell user={user} logout={logout} />;
}

// Portal pages a client account opens in the icon rail (§3.3: light by default, with the toggle): client Storage (HD-9).
// The rail carries no theme toggle there (themeless) and the tier is held here, ABOVE the rail, so it is applied after
// the rail's own Console theme on every mount (a parent's effects run after its children's); the page shows the toggle
// (usePortalRailTier). Staff on these paths, and client accounts everywhere else, get the plain RailShell. Crossing into
// or out of a portal path mounts the rail afresh (a different root): the rail has no state worth keeping across it.
const PORTAL_RAIL_PATHS = new Set(['/storage', '/assets']);
const PortalTierCtx = createContext(null);
export const usePortalRailTier = () => useContext(PortalTierCtx);

function PortalRail({ user, logout }) {
  const tier = usePortalTier(user);
  return <PortalTierCtx.Provider value={tier}><RailShell user={user} logout={logout} themeless /></PortalTierCtx.Provider>;
}

// Staff (expanded by default on Console, rail on Workbench) and external clients (icon rail, client nav).
// themeless: the page carries its own tier toggle (the review frame, HD-8), so the rail shows none.
export function RailShell({ user, logout, children, forceRail = false, themeless = false }) {
  const { aiProductionEnabled } = useAppConfig();
  const { theme, toggleTheme } = useTheme();
  const notes = useNotifications();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [stored, setStored] = usePersistedMode(user.id);
  const [notifOpen, setNotifOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const narrow = useMedia('(max-width: 1024px)');
  const phone = useMedia('(max-width: 768px)');
  const roles = user.roles || [];
  const isExternal = !!user.isExternal;
  const workbench = isWorkbench(pathname);
  const nav = isExternal ? CLIENT_NAV : staffNav({ isExternal, aiEnabled: aiProductionEnabled, hasTalent: hasTalentRole(user), workbench: workbenchItem(pathname, search) });
  const lockRail = isExternal || workbench || forceRail;
  const mode = phone ? 'expanded' : (narrow ? 'rail' : stored);
  const width = (lockRail || mode === 'rail') && !phone ? 56 : 216;
  const signOut = () => { logout(); navigate('/login'); };
  useEffect(() => { setDrawerOpen(false); setNotifOpen(false); }, [pathname]);
  // §3.2: the Workbench is dark, always. The shell carries the tier on its own root (so nothing under it can be flipped)
  // and on <html> while a Workbench route is open (dialogs and toasts render at the app root); leaving restores the user's.
  useEffect(() => {
    if (!workbench) return undefined;
    const html = document.documentElement; const before = html.getAttribute('data-theme');
    html.setAttribute('data-theme', 'dark');
    return () => { html.setAttribute('data-theme', before || theme); };
  }, [workbench]); // eslint-disable-line react-hooks/exhaustive-deps -- the user's theme is read when the Workbench closes
  const closeNotif = useCallback(() => setNotifOpen(false), []);

  return (
    <div className="app" data-theme={workbench ? 'dark' : undefined}>
      <Sidebar
        nav={nav} currentPath={pathname} onNavigate={to => navigate(to)}
        mode={mode} onModeChange={setStored} lockRail={lockRail && !phone}
        drawer={phone ? { open: drawerOpen, onClose: () => setDrawerOpen(false) } : undefined}
        label={isExternal ? 'Client navigation' : 'Primary'}
        onSearch={isExternal ? undefined : () => setPaletteOpen(true)}
        onNotifications={() => setNotifOpen(o => !o)} unread={notes.unread}
        onHelp={isExternal ? undefined : () => navigate('/assistant', { state: { helpMode: true } })}
        theme={workbench || themeless ? undefined : { value: theme, onToggle: toggleTheme }}
        account={{ name: user.name, initials: user.initials, role: roles[0], onOpen: () => navigate('/account') }}
        onSignOut={signOut}
      />
      {phone && !drawerOpen && (
        <button type="button" className="ui ui-btn ui-btn--secondary ui-btn--icon ui-shell-burger" aria-label="Open navigation" onClick={() => setDrawerOpen(true)}>
          <Icon icon={MenuIcon} />
        </button>
      )}
      {children || (isMigrated(pathname) || !isAppPath(pathname, { aiEnabled: aiProductionEnabled })   // HD-11.1: "Page not found" has the header
        ? <div className="ui-shell-main"><PageHeaderSlot /><Outlet /></div>
        : <Outlet />)}
      {notifOpen && <NotificationsPanel n={notes} onClose={closeNotif} railOffset={phone ? 8 : width + 8} />}
      {!isExternal && <ShellPalette nav={nav} open={paletteOpen} setOpen={setPaletteOpen} />}
      {/* The global Upload sheet (§2.6). Its queue is module-level, so it carries on across routes and shells. */}
      <UploadProvider />
    </div>
  );
}

// Talent-only users: the Portal Top bar (mark + wordmark + one action, Sign out) above the talent routes. §3.3 asks for
// both "one action" and the tier toggle, so the toggle sits beside Sign out as an icon button. The tier is the Portal one:
// light by default, the performer's choice kept per user (hf-portal-theme:<id>), set on <html> while the shell is open.
function PortalShell({ user, onSignOut }) {
  const navigate = useNavigate();
  const { tier, toggle } = usePortalTier(user);
  return (
    <div className="ui-portal-shell">
      <TopBar action={(
        <>
          <PortalTierToggle tier={tier} toggle={toggle} />
          <Button variant="quiet" portal icon={LogOut} onClick={() => { onSignOut(); navigate('/login'); }}>Sign out</Button>
        </>
      )} />
      <div className="ui-portal-shell__main"><Outlet /></div>
    </div>
  );
}
